/**
 * GIWAHS — Unified Data Client (Phase 1)
 * ---------------------------------------------------------------------
 * Single abstraction over both modes:
 *   Mode A (default) — Mock mode. Reads from /src/data/*.js synchronously.
 *                      Lead-capture writes log to console only.
 *   Mode B           — Supabase mode. Triggered when REACT_APP_SUPABASE_URL
 *                      + REACT_APP_SUPABASE_ANON_KEY are present.
 *
 * All exports return Promises so consumers (./hooks.js, ./mutations.js)
 * never need to know which mode is active.
 *
 * Shape contract for entities returned from this layer mirrors the legacy
 * mock-data shape exactly — this is intentional so existing components
 * continue to render without prop refactors.
 */
import { getSupabaseClient, isSupabaseConfigured } from '../lib/supabaseClient';
import { dispatch } from './notifications';
import { RFQ_STATUS_ORDER, nextStatus } from './rfqStatus';
import * as mock from '../data/mockData';
import { GUIDES, GUIDE_CATEGORIES } from '../data/contentData';
import { GUIDE_BODIES } from '../data/guideBodies';
import QRCode from 'qrcode';

export const MODE = isSupabaseConfigured() ? 'supabase' : 'mock';

// ─── Mock RFQ storage (localStorage-backed) ────────────────────────────
const MOCK_RFQS_KEY   = 'giwahs_rfqs_mock';
const MOCK_EVENTS_KEY = 'giwahs_rfq_events_mock';
const _readJSON = (k, fb) => { try { const r = localStorage.getItem(k); return r ? JSON.parse(r) : fb; } catch { return fb; } };
const _writeJSON = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };

const _saveMockRfq = (rfq) => {
  const all = _readJSON(MOCK_RFQS_KEY, {});
  all[rfq.reference_code] = rfq;
  _writeJSON(MOCK_RFQS_KEY, all);
};
const _readMockRfq = (ref) => _readJSON(MOCK_RFQS_KEY, {})[ref] || null;
const _updateMockRfqStatus = (ref, newStatus) => {
  const all = _readJSON(MOCK_RFQS_KEY, {});
  if (!all[ref]) return;
  all[ref].status = newStatus;
  all[ref].updated_at = new Date().toISOString();
  _writeJSON(MOCK_RFQS_KEY, all);
};
const _appendMockStatusEvent = (ref, event) => {
  const all = _readJSON(MOCK_EVENTS_KEY, {});
  all[ref] = [...(all[ref] || []), { id: `evt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`, ...event }];
  _writeJSON(MOCK_EVENTS_KEY, all);
};
const _readMockStatusEvents = (ref) => _readJSON(MOCK_EVENTS_KEY, {})[ref] || [];

// ─── helpers ───────────────────────────────────────────────────────────
const slugify = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const supplierFromRow = (r) => ({
  id: r.legacy_id || r.id,
  slug: r.slug,
  name: r.name,
  tagline: r.tagline,
  type: r.type_name || r.type_slug,
  country: r.country_name || r.country_slug,
  city: r.city,
  founded: r.founded_year,
  employees: r.employees_range,
  trustScore: r.trust_score,
  responseRate: r.response_rate_pct,
  responseTimeHours: r.response_time_hours,
  moqMin: r.moq_min,
  moqMax: r.moq_max,
  leadTimeDays: r.lead_time_days,
  productionCapacityPerMonth: r.production_capacity_monthly,
  pricingIndicator: r.pricing_indicator,
  heroImage: r.hero_image,
  gallery: r.gallery || [],
  categories: r.categories || [],
  specialties: r.specialties || [],
  certifications: r.certifications_list || [],
  shipping: r.shipping || [],
  exportMarkets: r.export_markets || [],
  badges: r.badges_list || [],
  knownBrandAssociations: r.brand_associations_list || [],
  featured: r.is_featured,
  featuredTier: r.featured_tier,
});

const productFromRow = (r) => ({
  id: r.legacy_id || r.id,
  slug: r.slug,
  name: r.name,
  category: r.category_slug,
  image: r.image,
  moq: r.moq,
  leadTime: r.lead_time,
  pricing: r.pricing_range,
  suppliers: r.supplier_legacy_ids || [],
  tags: r.tags || [],
  sourcingRegions: r.sourcing_regions || [],
  description: r.description,
});

const guideFromRow = (r) => ({
  slug: r.slug,
  title: r.title,
  summary: r.summary,
  category: r.category,
  categoryLabel: r.category_label,
  cover: r.cover,
  readingMin: r.read_time_min,
  author: r.author,
  related: {
    supplierSlug: r.related_supplier_slug,
    categorySlug: r.related_category_slug,
    country: r.related_country_slug,
  },
});

// ─── READS ─────────────────────────────────────────────────────────────

export const data = {
  // --- Suppliers ---
  async listSuppliers(filters = {}) {
    if (MODE === 'mock') {
      let xs = [...mock.SUPPLIERS];
      if (filters.country) xs = xs.filter(s => slugify(s.country) === filters.country);
      if (filters.category) xs = xs.filter(s => (s.categories || []).includes(filters.category));
      if (filters.type) xs = xs.filter(s => slugify(s.type) === filters.type);
      if (filters.featured) xs = xs.filter(s => s.featured === true);
      return xs;
    }
    const sb = getSupabaseClient();
    let q = sb.from('suppliers_full').select('*');
    if (filters.country) q = q.eq('country_slug', filters.country);
    if (filters.type) q = q.eq('type_slug', filters.type);
    if (filters.featured) q = q.eq('is_featured', true);
    if (filters.category) q = q.contains('categories', [filters.category]);
    const { data: rows, error } = await q.order('trust_score', { ascending: false }).limit(200);
    if (error) throw error;
    return rows.map(supplierFromRow);
  },

  async getSupplierBySlug(slug) {
    if (MODE === 'mock') return mock.getSupplierBySlug(slug) || null;
    const sb = getSupabaseClient();
    const { data: row, error } = await sb.from('suppliers_full').select('*').eq('slug', slug).maybeSingle();
    if (error) throw error;
    return row ? supplierFromRow(row) : null;
  },

  async getSupplierByLegacyId(legacyId) {
    if (MODE === 'mock') return mock.getSupplierById(legacyId) || null;
    const sb = getSupabaseClient();
    const { data: row, error } = await sb.from('suppliers_full').select('*').eq('legacy_id', legacyId).maybeSingle();
    if (error) throw error;
    return row ? supplierFromRow(row) : null;
  },

  async getFeaturedSuppliers() {
    return this.listSuppliers({ featured: true });
  },

  // --- Products ---
  async listProducts(filters = {}) {
    if (MODE === 'mock') {
      let xs = [...mock.PRODUCTS];
      if (filters.category) xs = xs.filter(p => p.category === filters.category);
      if (filters.country) xs = xs.filter(p => (p.sourcingRegions || []).some(r => slugify(r) === filters.country));
      return xs;
    }
    const sb = getSupabaseClient();
    let q = sb.from('products').select('*, product_suppliers(supplier:suppliers(legacy_id))');
    if (filters.category) q = q.eq('category_slug', filters.category);
    const { data: rows, error } = await q.limit(200);
    if (error) throw error;
    return rows.map(r => productFromRow({
      ...r,
      supplier_legacy_ids: (r.product_suppliers || []).map(ps => ps.supplier?.legacy_id).filter(Boolean),
    }));
  },

  async getProductBySlug(slug) {
    if (MODE === 'mock') return mock.getProductBySlug(slug) || null;
    const sb = getSupabaseClient();
    const { data: row, error } = await sb
      .from('products')
      .select('*, product_suppliers(supplier:suppliers(legacy_id))')
      .eq('slug', slug).maybeSingle();
    if (error) throw error;
    if (!row) return null;
    return productFromRow({
      ...row,
      supplier_legacy_ids: (row.product_suppliers || []).map(ps => ps.supplier?.legacy_id).filter(Boolean),
    });
  },

  // --- Guides ---
  async listGuides(filters = {}) {
    if (MODE === 'mock') {
      let xs = [...GUIDES];
      if (filters.category && filters.category !== 'all') xs = xs.filter(g => g.category === filters.category);
      return xs;
    }
    const sb = getSupabaseClient();
    let q = sb.from('sourcing_guides').select('*').eq('is_published', true);
    if (filters.category && filters.category !== 'all') q = q.eq('category', filters.category);
    const { data: rows, error } = await q.order('published_at', { ascending: false });
    if (error) throw error;
    return rows.map(guideFromRow);
  },

  async getGuideBySlug(slug) {
    if (MODE === 'mock') {
      const g = GUIDES.find(x => x.slug === slug);
      if (!g) return null;
      return { ...g, body: GUIDE_BODIES[slug] || null };
    }
    const sb = getSupabaseClient();
    const { data: row, error } = await sb
      .from('sourcing_guides')
      .select('*, guide_bodies(body)')
      .eq('slug', slug).maybeSingle();
    if (error) throw error;
    if (!row) return null;
    return { ...guideFromRow(row), body: row.guide_bodies?.body || null };
  },

  async getGuideCategories() {
    return GUIDE_CATEGORIES; // static reference list
  },

  // --- Reference ---
  async getCountries()         { return mock.COUNTRIES; },          // static is fine
  async getProductCategories() { return mock.PRODUCT_CATEGORIES; },
  async getCertifications()    { return mock.CERTIFICATIONS; },
  async getSupplierTypes()     { return mock.SUPPLIER_TYPES; },
};

// ─── WRITES (lead capture) ────────────────────────────────────────────

export const writes = {
  async submitRfq(payload) {
    let reference, rfqRow;
    if (MODE === 'mock') {
      console.info('[GIWAHS / mock mode] RFQ submitted:', payload);
      await new Promise(r => setTimeout(r, 400));
      reference = 'RFQ-MOCK-' + Date.now().toString(36).toUpperCase();
      // Persist the RFQ + the initial status event so the tracking page
      // can render it. (Frontend-only mock store. Real persistence lands
      // when Supabase is flipped on.)
      rfqRow = {
        reference_code: reference,
        status: 'submitted',
        created_at: new Date().toISOString(),
        ...payload,
      };
      _saveMockRfq(rfqRow);
      _appendMockStatusEvent(reference, {
        status: 'submitted',
        note: 'Your request has been received by the GIWAHS sourcing desk.',
        actor: 'system',
        created_at: rfqRow.created_at,
      });
    } else {
      const sb = getSupabaseClient();
      // Use the submit_rfq SECURITY DEFINER RPC so we don't need a SELECT
      // policy on rfqs for anon (privacy-by-default; buyers track via
      // get_rfq_tracking RPC + reference_code only).
      const { data, error } = await sb.rpc('submit_rfq', { payload });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      reference = row?.reference_code;
      if (!reference) throw new Error('submit_rfq returned no reference_code');
    }
    // Map the schema-shape RFQ payload onto the template-shape used by the
    // email subsystem (buyer_email → contact_email, etc.). This keeps the
    // DB insert clean while the email templates stay generic.
    // Generate the tracking QR data URL — the Edge Function embeds it as
    // an inline image. If QR generation fails we proceed without it; the
    // textual tracking link is always present as the universal fallback.
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://giwahs.com';
    const tracking_url = `${origin}/track?ref=${encodeURIComponent(reference)}`;
    let qr_data_url = '';
    try {
      qr_data_url = await QRCode.toDataURL(tracking_url, {
        errorCorrectionLevel: 'M', margin: 2, width: 300,
        color: { dark: '#0A0A0A', light: '#FFFFFF' },
      });
    } catch (e) {
      console.warn('[email QR] generation failed, falling back to text-only', e);
    }
    const emailPayload = {
      reference_code:      reference,
      status_label:        'Submitted',
      response_eta:        'within two business days',
      tracking_url,
      qr_data_url,
      support_email:       'hello@giwahs.com',
      contact_name:        payload.buyer_name,
      contact_email:       payload.buyer_email,
      company:             payload.buyer_company,
      category:            (payload.product_type || '').split(':')[0]?.trim() || payload.product_type,
      product_name:        (payload.product_type || '').split(':')[1]?.trim() || '',
      quantity:            payload.target_quantity,
      target_price:        payload.target_unit_price,
      destination_country: payload.shipping_destination,
      timeline:            payload.needed_by_date || '',
      notes:               payload.notes,
    };
    // Fire-and-forget notifications (never block the user).
    dispatch({ kind: 'rfq_confirmation', to: emailPayload.contact_email, payload: emailPayload });
    dispatch({ kind: 'rfq_admin_alert',  to: null,                       payload: emailPayload });
    return { ok: true, reference };
  },

  async submitContact(payload) {
    if (MODE === 'mock') {
      console.info('[GIWAHS / mock mode] Contact submitted:', payload);
      await new Promise(r => setTimeout(r, 400));
    } else {
      const sb = getSupabaseClient();
      const { error } = await sb.from('contact_messages').insert([payload]);
      if (error) throw error;
    }
    dispatch({ kind: 'contact_ack',         to: payload.email, payload });
    dispatch({ kind: 'contact_admin_alert', to: null,          payload });
    return { ok: true };
  },

  async submitSupplierApplication(payload) {
    if (MODE === 'mock') {
      console.info('[GIWAHS / mock mode] Supplier application submitted:', payload);
      await new Promise(r => setTimeout(r, 400));
    } else {
      const sb = getSupabaseClient();
      const { error } = await sb.from('supplier_applications').insert([payload]);
      if (error) throw error;
    }
    dispatch({ kind: 'supplier_application_confirmation', to: payload.contact_email, payload });
    dispatch({ kind: 'supplier_application_admin_alert',  to: null,                  payload });
    return { ok: true };
  },
};

// ─── USER-SCOPED PERSISTENCE (saved / compare) ─────────────────────────
// In mock mode the underlying store is per-user localStorage keys
// (already used by GiwahsContext anon path). In Supabase mode the store
// is the RLS-protected saved_suppliers / compare_suppliers tables.
// Supplier identity in the frontend is the `legacy_id` string (e.g.
// "sup-001"); the DB layer uses uuid PKs, so we translate transparently.

const USER_SAVED_KEY    = (uid) => `giwahs_saved_suppliers_user_${uid}`;
const USER_COMPARE_KEY  = (uid) => `giwahs_compare_suppliers_user_${uid}`;

const readJSON  = (key, fb) => { try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : fb; } catch { return fb; } };
const writeJSON = (key, v)  => { try { localStorage.setItem(key, JSON.stringify(v)); } catch {} };

// legacy_id <-> uuid cache for Supabase mode
const _supplierUuidByLegacy = new Map();
const _supplierLegacyByUuid = new Map();

const resolveSupplierUuid = async (legacyId) => {
  if (_supplierUuidByLegacy.has(legacyId)) return _supplierUuidByLegacy.get(legacyId);
  const sb = getSupabaseClient();
  const { data: row, error } = await sb.from('suppliers').select('id, legacy_id').eq('legacy_id', legacyId).maybeSingle();
  if (error) throw error;
  if (!row) throw new Error(`Supplier with legacy_id '${legacyId}' not found.`);
  _supplierUuidByLegacy.set(row.legacy_id, row.id);
  _supplierLegacyByUuid.set(row.id, row.legacy_id);
  return row.id;
};

export const userData = {
  async listSaved(userId) {
    if (!userId) return [];
    if (MODE === 'mock') return readJSON(USER_SAVED_KEY(userId), []);
    const sb = getSupabaseClient();
    const { data: rows, error } = await sb
      .from('saved_suppliers')
      .select('supplier:suppliers(legacy_id)')
      .order('saved_at', { ascending: false });
    if (error) throw error;
    return (rows || []).map(r => r.supplier?.legacy_id).filter(Boolean);
  },

  async addSaved(userId, supplierLegacyId) {
    if (!userId) throw new Error('Not signed in.');
    if (MODE === 'mock') {
      const cur = readJSON(USER_SAVED_KEY(userId), []);
      if (!cur.includes(supplierLegacyId)) writeJSON(USER_SAVED_KEY(userId), [...cur, supplierLegacyId]);
      return { ok: true };
    }
    const sb = getSupabaseClient();
    const supplierUuid = await resolveSupplierUuid(supplierLegacyId);
    const { error } = await sb.from('saved_suppliers')
      .upsert([{ user_id: userId, supplier_id: supplierUuid }], { onConflict: 'user_id,supplier_id' });
    if (error) throw error;
    return { ok: true };
  },

  async removeSaved(userId, supplierLegacyId) {
    if (!userId) throw new Error('Not signed in.');
    if (MODE === 'mock') {
      const cur = readJSON(USER_SAVED_KEY(userId), []);
      writeJSON(USER_SAVED_KEY(userId), cur.filter(id => id !== supplierLegacyId));
      return { ok: true };
    }
    const sb = getSupabaseClient();
    const supplierUuid = await resolveSupplierUuid(supplierLegacyId);
    const { error } = await sb.from('saved_suppliers')
      .delete().eq('user_id', userId).eq('supplier_id', supplierUuid);
    if (error) throw error;
    return { ok: true };
  },

  async listCompare(userId) {
    if (!userId) return [];
    if (MODE === 'mock') return readJSON(USER_COMPARE_KEY(userId), []);
    const sb = getSupabaseClient();
    const { data: rows, error } = await sb
      .from('compare_suppliers')
      .select('supplier:suppliers(legacy_id)')
      .order('added_at', { ascending: true });
    if (error) throw error;
    return (rows || []).map(r => r.supplier?.legacy_id).filter(Boolean);
  },

  async addCompare(userId, supplierLegacyId) {
    if (!userId) throw new Error('Not signed in.');
    if (MODE === 'mock') {
      const cur = readJSON(USER_COMPARE_KEY(userId), []);
      if (cur.length >= 4) throw new Error('Compare list is full (max 4).');
      if (!cur.includes(supplierLegacyId)) writeJSON(USER_COMPARE_KEY(userId), [...cur, supplierLegacyId]);
      return { ok: true };
    }
    const sb = getSupabaseClient();
    // Enforce 4-item cap server-side (RLS scopes to current user, but
    // we also filter explicitly so intent is obvious and a misconfigured
    // RLS policy can't let one user's overshoot block another's adds).
    const { count, error: countErr } = await sb
      .from('compare_suppliers')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);
    if (countErr) throw countErr;
    if ((count || 0) >= 4) throw new Error('Compare list is full (max 4).');
    const supplierUuid = await resolveSupplierUuid(supplierLegacyId);
    const { error } = await sb.from('compare_suppliers')
      .upsert([{ user_id: userId, supplier_id: supplierUuid }], { onConflict: 'user_id,supplier_id' });
    if (error) throw error;
    return { ok: true };
  },

  async removeCompare(userId, supplierLegacyId) {
    if (!userId) throw new Error('Not signed in.');
    if (MODE === 'mock') {
      const cur = readJSON(USER_COMPARE_KEY(userId), []);
      writeJSON(USER_COMPARE_KEY(userId), cur.filter(id => id !== supplierLegacyId));
      return { ok: true };
    }
    const sb = getSupabaseClient();
    const supplierUuid = await resolveSupplierUuid(supplierLegacyId);
    const { error } = await sb.from('compare_suppliers')
      .delete().eq('user_id', userId).eq('supplier_id', supplierUuid);
    if (error) throw error;
    return { ok: true };
  },

  async clearCompare(userId) {
    if (!userId) throw new Error('Not signed in.');
    if (MODE === 'mock') { writeJSON(USER_COMPARE_KEY(userId), []); return { ok: true }; }
    const sb = getSupabaseClient();
    const { error } = await sb.from('compare_suppliers').delete().eq('user_id', userId);
    if (error) throw error;
    return { ok: true };
  },

  /**
   * One-shot anonymous → user migration. Reads the anon localStorage
   * lists, adds each supplier to the user's saved/compare via the active
   * mode, then clears the anon keys.
   */
  async mergeAnonymousIntoUser(userId, { anonSavedKey = 'giwahs_saved_suppliers', anonCompareKey = 'giwahs_compare_suppliers' } = {}) {
    if (!userId) return { saved: 0, compared: 0 };
    const anonSaved   = readJSON(anonSavedKey,   []);
    const anonCompare = readJSON(anonCompareKey, []);
    let savedCount = 0, comparedCount = 0;
    for (const id of anonSaved) {
      try { await this.addSaved(userId, id); savedCount++; } catch (e) { console.warn('[merge anon→user] saved', id, e?.message); }
    }
    for (const id of anonCompare.slice(0, 4)) {
      try { await this.addCompare(userId, id); comparedCount++; } catch (e) { console.warn('[merge anon→user] compare', id, e?.message); }
    }
    try { localStorage.removeItem(anonSavedKey);   } catch {}
    try { localStorage.removeItem(anonCompareKey); } catch {}
    return { saved: savedCount, compared: comparedCount };
  },
};

// ─── RFQ TRACKING (read RFQ + status timeline; admin updates) ──────────
// In Supabase mode we call the security-definer RPC `get_rfq_tracking`
// which accepts the reference_code as a bearer secret and returns the
// RFQ row + its full events array. This sidesteps the auth.uid()-only
// RLS on `rfqs` so anonymous tracking-page visits work, while still
// keeping the rows themselves invisible to unauthenticated SELECT *.
let _lastTrackingFetch = { ref: null, data: null };

export const rfqTracking = {
  async _fetchTracking(reference) {
    const sb = getSupabaseClient();
    const { data: rows, error } = await sb.rpc('get_rfq_tracking', { p_ref: reference });
    if (error) throw error;
    const row = Array.isArray(rows) ? rows[0] : rows;
    return row || null;
  },

  /**
   * Fetch a single RFQ by reference_code. Returns null if not found.
   */
  async getByReference(reference) {
    if (!reference) return null;
    if (MODE === 'mock') return _readMockRfq(reference);
    const row = await this._fetchTracking(reference);
    if (!row) return null;
    _lastTrackingFetch = { ref: reference, data: row };
    // Return the RFQ-shaped fields the page expects.
    return row;
  },

  async listStatusEvents(reference) {
    if (!reference) return [];
    if (MODE === 'mock') return _readMockStatusEvents(reference);
    // Reuse the cached fetch if the page just loaded the RFQ — saves a
    // roundtrip while still being correct (the RPC returns both in one go).
    let row = _lastTrackingFetch.ref === reference ? _lastTrackingFetch.data : null;
    if (!row) row = await this._fetchTracking(reference);
    if (!row) return [];
    return Array.isArray(row.events_json) ? row.events_json : [];
  },

  /**
   * Mock-only admin helper for demos / dev. Advances the RFQ to the next
   * lifecycle status and appends a status_event row. Surfaces in the UI
   * only behind a query-string toggle so production buyers never see it.
   */
  async advanceMockStatus(reference, { actor = 'admin', note } = {}) {
    if (MODE !== 'mock') throw new Error('advanceMockStatus is mock-only. Use the admin RPC in live mode.');
    const rfq = _readMockRfq(reference);
    if (!rfq) throw new Error('RFQ not found.');
    const next = nextStatus(rfq.status);
    if (!next) return { ok: false, reason: 'terminal' };
    _updateMockRfqStatus(reference, next);
    _appendMockStatusEvent(reference, {
      status: next,
      actor,
      note: note || _defaultStatusNote(next),
      created_at: new Date().toISOString(),
    });
    return { ok: true, status: next };
  },

  /**
   * Mock-only helper to seed a fully-walked timeline (every status with
   * a few minutes between events) — useful for screenshots, demos and the
   * design QA loop.
   */
  async seedMockFullTimeline(reference) {
    if (MODE !== 'mock') throw new Error('seed is mock-only.');
    const rfq = _readMockRfq(reference);
    if (!rfq) throw new Error('RFQ not found.');
    const start = Date.now() - 1000 * 60 * 60 * 18; // 18h ago
    const stepGapMs = (1000 * 60 * 60 * 18) / RFQ_STATUS_ORDER.length;
    // Reset events array then walk.
    const all = _readJSON(MOCK_EVENTS_KEY, {});
    all[reference] = RFQ_STATUS_ORDER.map((status, i) => ({
      id: `evt_seed_${i}`,
      status,
      actor: i === 0 ? 'system' : 'admin',
      note: _defaultStatusNote(status),
      created_at: new Date(start + i * stepGapMs).toISOString(),
    }));
    _writeJSON(MOCK_EVENTS_KEY, all);
    _updateMockRfqStatus(reference, RFQ_STATUS_ORDER[RFQ_STATUS_ORDER.length - 1]);
    return { ok: true };
  },

  /**
   * Buyer accepts or rejects a supplier quote on their own RFQ.
   * Calls the SECURITY DEFINER RPC `buyer_decide_on_quote`.
   * - Authorization: JWT email must match the RFQ's buyer_email
   *   (enforced server-side).
   * - On accept, the RPC also auto-rejects every other live quote on the
   *   same RFQ and closes the RFQ. The dataClient layer then fires the
   *   `quote_accepted` / `quote_rejected` mock-email hooks for any affected
   *   supplier(s) so the supplier surface stays in sync.
   *
   * @param {object} args
   * @param {string} args.quoteId      Target quote UUID.
   * @param {boolean} args.accept      true = accept, false = reject.
   * @param {string} [args.note]       Optional reason / note from the buyer.
   * @param {object} [args.rfq]        The full RFQ row (used to build email payloads;
   *                                   if absent the function will re-fetch via tracking).
   * @returns {Promise<{ok, quote_status, rfq_status, supplier_slug, reference_code}>}
   */
  async decideOnQuote({ quoteId, accept, note, rfq }) {
    if (MODE !== 'supabase') {
      throw new Error('Buyer decisions require live Supabase mode.');
    }
    const sb = getSupabaseClient();
    const { data, error } = await sb.rpc('buyer_decide_on_quote', {
      p_quote_id: quoteId,
      p_accept:   !!accept,
      p_note:     note || null,
    });
    if (error) throw error;

    // Best-effort: re-load tracking so the caller gets the freshest quotes
    // (incl. cascading rejects) and dispatch supplier notifications.
    try {
      const reference = data?.reference_code || rfq?.reference_code;
      if (reference) {
        const fresh = await this._fetchTracking(reference);
        const quotes = Array.isArray(fresh?.quotes_json) ? fresh.quotes_json : [];
        const acceptedQuote = quotes.find(q => q.id === quoteId);
        const buyer_name    = fresh?.buyer_name || rfq?.buyer_name || '';
        const buyer_company = fresh?.buyer_company || rfq?.buyer_company || '';
        const refCode       = fresh?.reference_code || reference;

        if (accept) {
          // 1. Notify the accepted supplier.
          if (acceptedQuote) {
            dispatch({
              kind: 'quote_accepted',
              to:   null, // supplier email is not exposed via tracking — handled by Edge Function in live mode
              payload: {
                reference_code: refCode,
                supplier_slug:  acceptedQuote.supplier_slug,
                supplier_name:  acceptedQuote.supplier_name,
                unit_price:     acceptedQuote.unit_price,
                moq:            acceptedQuote.moq,
                lead_time_days: acceptedQuote.lead_time_days,
                buyer_name,
                buyer_company,
                buyer_note:     note || '',
              },
            });
          }
          // 2. Notify every other supplier whose quote was auto-rejected.
          for (const q of quotes) {
            if (q.id === quoteId) continue;
            if (q.status !== 'rejected') continue;
            dispatch({
              kind: 'quote_rejected',
              to:   null,
              payload: {
                reference_code: refCode,
                supplier_slug:  q.supplier_slug,
                supplier_name:  q.supplier_name,
                unit_price:     q.unit_price,
                moq:            q.moq,
                lead_time_days: q.lead_time_days,
                auto_rejected:  true,
                buyer_note:     '',
              },
            });
          }
        } else if (acceptedQuote) {
          // Manual reject of a single quote.
          dispatch({
            kind: 'quote_rejected',
            to:   null,
            payload: {
              reference_code: refCode,
              supplier_slug:  acceptedQuote.supplier_slug,
              supplier_name:  acceptedQuote.supplier_name,
              unit_price:     acceptedQuote.unit_price,
              moq:            acceptedQuote.moq,
              lead_time_days: acceptedQuote.lead_time_days,
              auto_rejected:  false,
              buyer_note:     note || '',
            },
          });
        }
      }
    } catch (e) {
      // Notification failures must never break the decision.
      console.warn('[dataClient] post-decision notify skipped:', e?.message);
    }

    return data || { ok: true };
  },
};

const _defaultStatusNote = (status) => {
  switch (status) {
    case 'submitted':              return 'Your request has been received by the GIWAHS sourcing desk.';
    case 'in_queue':               return 'Placed in the active sourcing queue.';
    case 'reviewing_requirements': return 'A sourcing lead is reading your brief and flagging clarification needs.';
    case 'supplier_shortlisting':  return 'Matching brief against verified factories.';
    case 'quotes_requested':       return 'Shortlisted factories have been briefed.';
    case 'quotes_received':        return 'Factory quotes are landing. Aggregating now.';
    case 'buyer_review':           return 'A curated comparison is ready for your review.';
    case 'closed':                 return 'This RFQ has been closed.';
    default:                       return 'Status updated.';
  }
};

export default data;
